import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Bot } from 'lucide-react';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { useLcpReady } from '../../../hooks/useLcpReady';
import { BUILD_SURFACES } from './agentBuildSurfaces';

const ROTATE_MS = 2600;
const PANEL_ID = 'agent-surface-panel';

/**
 * Studio hero visual: a browser-framed product window that cycles through the
 * eight surfaces a learner actually works in — Lab → Trace → Knowledge → Tutor →
 * MCP → Evals → Guardrails → A2A. The tab strip stays on one row and scrolls
 * horizontally when it overflows; the active tab is centered in the strip only
 * (never scrolls the page). Auto-rotates
 * (pauses on hover/focus, stops once a tab is chosen, and never auto-plays under
 * prefers-reduced-motion). Content is a visual placeholder.
 */
export function AgentBuildWindow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [manual, setManual] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const lcpReady = useLcpReady();
  const tablistRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const autoRotating = lcpReady && !reducedMotion && !paused && !manual;

  const scrollActiveTabIntoStrip = (tabIndex: number) => {
    const tablist = tablistRef.current;
    const tab = tabRefs.current[tabIndex];
    if (!tablist || !tab) return;

    // offsetLeft is relative to a positioned ancestor (e.g. studio-hero-section),
    // not the tablist — use viewport rects so scroll math stays inside the strip.
    const listRect = tablist.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const tabOffsetInList = tabRect.left - listRect.left + tablist.scrollLeft;
    const targetScroll = tabOffsetInList + tabRect.width / 2 - listRect.width / 2;
    const maxScroll = tablist.scrollWidth - tablist.clientWidth;
    tablist.scrollLeft = Math.max(0, Math.min(targetScroll, maxScroll));
  };

  useEffect(() => {
    if (!autoRotating) return;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % BUILD_SURFACES.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [autoRotating]);

  useLayoutEffect(() => {
    scrollActiveTabIntoStrip(index);
  }, [index]);

  const Active = BUILD_SURFACES[index].Surface;
  const activeTabId = `agent-tab-${BUILD_SURFACES[index].id}`;

  const selectTab = (i: number) => {
    setIndex(i);
    setManual(true);
  };

  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const count = BUILD_SURFACES.length;
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % count;
    else if (e.key === 'ArrowLeft') next = (index - 1 + count) % count;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = count - 1;
    else return;
    e.preventDefault();
    selectTab(next);
    tabRefs.current[next]?.focus({ preventScroll: true });
  };

  return (
    <div
      className="agent-build-window"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      style={{
        width: '100%',
        minWidth: 0,
        background: '#fff',
        borderRadius: '20px',
        boxShadow: '0 24px 60px rgba(0,0,0,.45)',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,.08)',
      }}
    >
      {/* Title bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 16px',
          borderBottom: '1px solid #eef2f6',
          background: '#fbfcfe',
        }}
      >
        {/* macOS window-chrome colors — intentional, no token equivalent. */}
        <span style={{ display: 'flex', gap: '6px' }} aria-hidden="true">
          <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#ff5f57' }} />
          <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#febc2e' }} />
          <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: '#28c840' }} />
        </span>
        <span
          style={{
            marginLeft: '6px',
            width: '24px',
            height: '24px',
            borderRadius: '7px',
            background: 'var(--gradient-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <Bot size={14} />
        </span>
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--marketing-heading)' }}>Noah&apos;s agent</span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            fontWeight: 700,
            padding: '3px 9px',
            borderRadius: '999px',
            background: 'rgba(20,184,166,.12)',
            color: 'var(--primary-dark)',
          }}
        >
          v9
        </span>
      </div>

      {/* Capability rail — single row; scrolls horizontally when tabs overflow. */}
      <div
        ref={tablistRef}
        role="tablist"
        aria-label="Agent build surfaces"
        onKeyDown={onTabKeyDown}
        className="agent-build-tablist"
      >
        {BUILD_SURFACES.map((surface, i) => {
          const active = i === index;
          return (
            <button
              key={surface.id}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`agent-tab-${surface.id}`}
              aria-selected={active}
              aria-controls={PANEL_ID}
              tabIndex={active ? 0 : -1}
              onClick={() => selectTab(i)}
              className="agent-build-tab"
              style={{
                padding: '11px 12px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12.5px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                background: active ? 'rgba(20,184,166,.12)' : 'transparent',
                color: active ? 'var(--primary-dark)' : 'var(--marketing-soft)',
              }}
            >
              {surface.label}
            </button>
          );
        })}
      </div>

      {/* Surface body — fixed min-height so cycling never jumps the layout */}
      {/* tabIndex=0 so keyboard users can reach the panel — surfaces have no focusable children. */}
      <div
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={activeTabId}
        tabIndex={0}
        className="agent-surface-panel"
        style={{ padding: '18px', minHeight: '252px' }}
      >
        <div
          key={index}
          className={reducedMotion ? 'agent-surface-content' : 'agent-surface-content agent-surface-content--animate'}
        >
          <Active />
        </div>
      </div>

      <style>{`
        .agent-build-tablist {
          display: flex;
          flex-wrap: nowrap;
          gap: 4px;
          padding: 8px 10px;
          border-bottom: 1px solid #eef2f6;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .agent-build-tablist::-webkit-scrollbar { display: none; }
        .agent-build-tab {
          flex-shrink: 0;
          padding: 11px 12px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 12.5px;
          font-weight: 600;
          white-space: nowrap;
          transition: background .15s ease, color .15s ease;
        }
        .agent-build-tab:hover { background: rgba(20,184,166,.08); color: var(--primary-dark); }
        .agent-build-tab:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) {
          .agent-build-tab { transition: none; }
          .agent-build-tablist { scroll-behavior: auto; }
          .agent-surface-content--animate { animation: none; }
        }
        @media (prefers-reduced-motion: no-preference) {
          .agent-build-tablist { scroll-behavior: smooth; }
          .agent-surface-content--animate {
            animation: agentSurfaceIn 0.32s ease;
          }
          @keyframes agentSurfaceIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        }
        @media (max-width: 767px) {
          .agent-build-tab {
            padding: 10px 11px !important;
            font-size: 11.5px !important;
          }
          .agent-surface-panel {
            padding: 14px !important;
            min-height: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
